import { LoadingOutlined } from "@ant-design/icons";
import { Drawer, Spin } from "antd";
import { allTemplatesSelector } from "appredux/selectors";
import { useAntdMessage } from "hooks/useAntdMessage";
import { useSendMessage } from "queries/conversation";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { buildConversationMessagePayload } from "utils/buildConversationMessagePayload";
import MeetingReminderForm from "./MeetingReminderForm";
import { resolveSchedulePhoneNumberId } from "./scheduleDetailResolvers";

const MeetingReminderSendDrawer = ({
  open,
  onClose,
  scheduleDetail,
  timeSource,
  detailLoading = false,
}) => {
  const { t } = useTranslation();
  const { message } = useAntdMessage();
  const mutation = useSendMessage();
  const allTemplates = useSelector(allTemplatesSelector);

  const phoneNumberId = resolveSchedulePhoneNumberId(
    scheduleDetail,
    timeSource
  );

  const zoomTemplates = useMemo(() => {
    if (phoneNumberId == null) return [];
    const pn = Number(phoneNumberId);
    return (allTemplates || [])
      .filter(
        (tpl) =>
          Number(tpl.phone_number_id) === pn &&
          tpl.is_active !== 0
      )
      .sort((a, b) => {
        const aZoom = String(a.title || "").toLowerCase().includes("zoom");
        const bZoom = String(b.title || "").toLowerCase().includes("zoom");
        if (aZoom && !bZoom) return -1;
        if (!aZoom && bZoom) return 1;
        return 0;
      });
  }, [allTemplates, phoneNumberId]);

  const submitHandler = useCallback(
    (values) => {
      if (phoneNumberId == null) {
        message.error(t("meeting_management.reminder_missing_phone_number"));
        return;
      }
      const payload = buildConversationMessagePayload(values, phoneNumberId);
      mutation.mutate(payload, {
        onSuccess: () => {
          message.success(t("meeting_management.reminder_sent"));
          onClose();
        },
        onError: (err) => {
          message.error(
            err?.response?.data?.message ||
              t("meeting_management.reminder_send_failed")
          );
        },
      });
    },
    [message, mutation, onClose, phoneNumberId, t]
  );

  const showForm = Boolean(scheduleDetail);
  const showInitialLoading = open && !showForm && detailLoading;
  const showLoadFailed = open && !showForm && !detailLoading;

  return (
    <Drawer
      title={t("meeting_management.send_reminder")}
      placement="left"
      width="100%"
      open={open}
      onClose={onClose}
      destroyOnHidden
      zIndex={1100}
      getContainer={() => document.body}
      styles={{
        header: {
          background: "#FAFAFA",
          paddingTop: 19.5,
          paddingBottom: 19.5,
          borderLeft: "1px solid #E6E7EB",
        },
        wrapper: { zIndex: 1100 },
      }}
    >
      <Spin
        indicator={<LoadingOutlined spin />}
        spinning={mutation.isPending || showInitialLoading}
        tip="Loading..."
        size="large"
      >
        {showForm ? (
          <MeetingReminderForm
            key={String(scheduleDetail?.id ?? "reminder")}
            onSubmit={submitHandler}
            onCancel={onClose}
            scheduleDetail={scheduleDetail}
            timeSource={timeSource}
            zoomTemplates={zoomTemplates}
            phoneNumberId={phoneNumberId}
          />
        ) : showLoadFailed ? (
          <p className="text-center text-neutral-500">
            {t("meeting_management.reminder_detail_load_error")}
          </p>
        ) : null}
      </Spin>
    </Drawer>
  );
};

export default MeetingReminderSendDrawer;
